const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/;
const SCHEME_PATTERN = /^([a-zA-Z][a-zA-Z\d+.-]*):/;
const SAFE_HASH_PATTERN = /^#[A-Za-z0-9_-]+$/;

const MAP_URL_HOSTS = [
  'google.com',
  'google.pt',
  'maps.app.goo.gl',
  'openstreetmap.org',
];

type UrlSafetyOptions = {
  allowHash?: boolean;
  allowHttp?: boolean;
  allowRelative?: boolean;
  allowedHosts?: readonly string[];
};

function isLocalHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return (
    normalized === 'localhost' ||
    normalized.endsWith('.localhost') ||
    normalized === '0.0.0.0' ||
    normalized === '::1' ||
    normalized === '[::1]' ||
    normalized === '127.0.0.1' ||
    normalized.startsWith('127.')
  );
}

function isAllowedHost(hostname: string, allowedHosts: readonly string[]): boolean {
  const normalized = hostname.toLowerCase();
  return allowedHosts.some((host) => {
    const allowed = host.toLowerCase();
    return normalized === allowed || normalized.endsWith(`.${allowed}`);
  });
}

function isSafeRelativeUrl(value: string): boolean {
  if (value.startsWith('//') || value.startsWith('\\')) return false;
  if (SCHEME_PATTERN.test(value)) return false;
  return value.startsWith('/') || value.startsWith('./') || value.startsWith('../');
}

export function sanitizeEditableUrl(
  value: unknown,
  options: UrlSafetyOptions = {},
): string | undefined {
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed || CONTROL_CHARACTER_PATTERN.test(trimmed)) return undefined;

  if (trimmed.startsWith('#')) {
    return options.allowHash && SAFE_HASH_PATTERN.test(trimmed) ? trimmed : undefined;
  }

  if (options.allowRelative && isSafeRelativeUrl(trimmed)) {
    return trimmed;
  }

  const scheme = trimmed.match(SCHEME_PATTERN)?.[1]?.toLowerCase();
  if (!scheme || (scheme !== 'https' && scheme !== 'http')) return undefined;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return undefined;
  }

  if (parsed.protocol === 'http:' && !options.allowHttp && !isLocalHostname(parsed.hostname)) {
    return undefined;
  }

  if (options.allowedHosts && !isAllowedHost(parsed.hostname, options.allowedHosts)) {
    return undefined;
  }

  return parsed.href;
}

export function sanitizeMediaUrl(value: unknown): string | undefined {
  return sanitizeEditableUrl(value, {
    allowRelative: true,
  });
}

export function sanitizeLinkUrl(
  value: unknown,
  options: Omit<UrlSafetyOptions, 'allowHttp'> = {},
): string | undefined {
  return sanitizeEditableUrl(value, options);
}

export function sanitizeMapUrl(value: unknown): string | undefined {
  return sanitizeEditableUrl(value, {
    allowedHosts: MAP_URL_HOSTS,
  });
}

export function sanitizePhoneHref(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (CONTROL_CHARACTER_PATTERN.test(value)) return undefined;

  const phone = value.trim().replace(/[^\d+]/g, '');
  if (!/^\+?\d{3,15}$/.test(phone)) return undefined;
  return `tel:${phone}`;
}

export function sanitizeWhatsAppHref(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (CONTROL_CHARACTER_PATTERN.test(value)) return undefined;

  const digits = value.replace(/\D/g, '');
  if (!/^\d{5,15}$/.test(digits)) return undefined;
  return `https://wa.me/${digits}`;
}
